// The DLT-approved wordings.
//
// The `body` field is not a message you compose — it is a transcription of
// text already approved on the DLT portal. The operator compares what it
// delivers against that approved text character by character and silently
// discards a mismatch while MSG91 still reports success, so this screen's job
// is to make any divergence visible BEFORE a message is sent: what the patient
// would actually read, whether the fixed words survived the fill, and what it
// costs.
import { useMemo, useState } from 'react';
import { MessageSquareText, Plus, TriangleAlert } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Input, Textarea } from '@/components/ui/Input';
import { Switch } from '@/components/ui/Switch';
import { FormField } from '@/components/ui/FormField';
import { LoadingState, ErrorState, EmptyState } from '@/components/ui/States';
import { ApiError } from '@/lib/api/client';
import { previewTemplate, testSend } from './api';
import { nonGsmCharacters } from './gsm';
import { useTemplates, useSaveTemplate, useDeactivateTemplate } from './hooks';
import type { SmsTemplate, TemplatePreview, TestSendResult } from './types';

const BLANK: SmsTemplate = {
  id: 0, template_key: '', name: '', body: '', variable_count: 0, variable_count_stale: false,
  dlt_template_id: null, msg91_template_id: null, active: true, notes: null,
  ready: false, blockers: [], encoding: 'GSM7', non_gsm_characters: [],
  updated_at: null, updated_by: null,
};

/** Blanks are matched positionally — the type hint inside is not an identity. */
const PLACEHOLDER = /\{#\w+#\}/g;
const countBlanks = (body: string) => (body.match(PLACEHOLDER) || []).length;

export default function TemplatesTab() {
  const templates = useTemplates();
  const save = useSaveTemplate();
  const deactivate = useDeactivateTemplate();

  const [editing, setEditing] = useState<SmsTemplate | null>(null);
  const [values, setValues] = useState<string[]>([]);
  const [preview, setPreview] = useState<TemplatePreview | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [mobile, setMobile] = useState('');
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState<TestSendResult | null>(null);
  const [sendError, setSendError] = useState<string | null>(null);

  const blanks = countBlanks(editing?.body ?? '');

  // Recomputed as the body is typed so an admin transcribing approved text
  // sees the cost consequence of a pasted rupee sign or curly apostrophe
  // immediately, not after saving. The server measures it again authoritatively
  // on save and on preview — see gsm.ts.
  const bodyWarnings = useMemo(() => nonGsmCharacters(editing?.body ?? ''), [editing?.body]);

  function open(tpl: SmsTemplate) {
    setEditing({ ...tpl });
    setValues(Array.from({ length: countBlanks(tpl.body) }, (_, i) => `Sample ${i + 1}`));
    setPreview(null);
    setSendResult(null);
    setSendError(null);
    setMobile('');
  }

  function edit<K extends keyof SmsTemplate>(k: K, v: SmsTemplate[K]) {
    setEditing((p) => {
      if (!p) return p;
      const next = { ...p, [k]: v };
      // Keep the sample-value boxes in step with the blanks in the wording, so
      // the Validate button can never be pressed with the wrong number of them.
      if (k === 'body') {
        const n = countBlanks(next.body);
        setValues((old) => Array.from({ length: n }, (_, i) => old[i] ?? `Sample ${i + 1}`));
        setPreview(null);
      }
      return next;
    });
  }

  async function runPreview() {
    if (!editing || !editing.id) return;
    setPreviewing(true);
    setPreview(null);
    try {
      setPreview(await previewTemplate(editing.id, values));
    } catch (e) {
      setPreview({ valid: false, error: e instanceof ApiError ? e.message : 'Could not build a preview.', variable_count: blanks });
    } finally {
      setPreviewing(false);
    }
  }

  async function runTestSend() {
    if (!editing || !editing.id) return;
    setSending(true);
    setSendResult(null);
    setSendError(null);
    try {
      setSendResult(await testSend(editing.id, mobile, values));
    } catch (e) {
      setSendError(e instanceof ApiError ? e.message : 'The test send could not be run.');
    } finally {
      setSending(false);
    }
  }

  async function onSave() {
    if (!editing) return;
    const data = {
      template_key: editing.template_key,
      name: editing.name,
      body: editing.body,
      dlt_template_id: editing.dlt_template_id,
      msg91_template_id: editing.msg91_template_id,
      active: editing.active,
      notes: editing.notes,
    };
    const saved = await save.mutateAsync({ id: editing.id || undefined, data });
    setEditing(saved);
  }

  if (templates.isLoading) return <LoadingState label="Loading SMS wordings…" />;
  if (templates.isError) {
    return (
      <ErrorState
        message={templates.error instanceof ApiError ? templates.error.message : 'Could not load SMS wordings'}
        onRetry={() => templates.refetch()}
      />
    );
  }

  const rows = templates.data ?? [];

  // ── Editor ───────────────────────────────────────────────────
  if (editing) {
    return (
      <div className="max-w-3xl space-y-4">
        <button onClick={() => setEditing(null)} className="text-xs font-medium text-teal hover:underline">
          ‹ Back to wordings
        </button>

        <Card>
          <CardHeader>
            <CardTitle>{editing.id ? editing.name : 'New wording'}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid2">
              <FormField label="Name" required>
                <Input value={editing.name} onChange={(e) => edit('name', e.target.value)} placeholder="Appointment confirmed" />
              </FormField>
              <FormField label="Event key" required>
                <Input
                  value={editing.template_key}
                  onChange={(e) => edit('template_key', e.target.value)}
                  placeholder="appointment_booked"
                  disabled={!!editing.id}
                />
                <div className="mt-1 text-[11px] text-ink-muted">
                  {editing.id
                    ? 'Fixed once created — delivery log rows refer to it.'
                    : 'The name of the event this wording is for.'}
                </div>
              </FormField>
            </div>

            <FormField
              label="Registered wording"
              required
              error={save.error instanceof ApiError ? save.error.fieldErrors.body : undefined}
            >
              <Textarea
                value={editing.body}
                onChange={(e) => edit('body', e.target.value)}
                rows={4}
                className="font-mono text-[12px]"
                placeholder="Dear {#var#}, your appointment with {#var#} is confirmed on {#var#}."
              />
              <div className="mt-1 text-[11px] text-ink-muted">
                Paste this from the DLT portal exactly as approved — including spacing and punctuation.
                Blanks are written <code>{'{#var#}'}</code> and are filled strictly in order.
                This wording has <strong>{blanks}</strong> blank{blanks === 1 ? '' : 's'}.
              </div>
            </FormField>

            {bodyWarnings.length > 0 && (
              <div className="rounded-xl border border-warn-soft bg-warn-soft p-3 text-[12px] text-warn-ink">
                <div className="flex items-start gap-2">
                  <TriangleAlert size={15} className="mt-px shrink-0" />
                  <div>
                    This wording contains <strong>{bodyWarnings.map((c) => `"${c}"`).join(', ')}</strong>, which
                    is outside the plain SMS alphabet. One such character drops the budget from 160
                    characters per message to 70, roughly tripling the cost of every message sent from
                    this template — for as long as it exists. Prefer <code>Rs</code> over{' '}
                    <code>₹</code> and a straight apostrophe over a curly one.
                  </div>
                </div>
              </div>
            )}

            <div className="grid2">
              <FormField label="DLT template ID">
                <Input
                  value={editing.dlt_template_id ?? ''}
                  onChange={(e) => edit('dlt_template_id', e.target.value || null)}
                  placeholder="1077032540036877754"
                  className="font-mono text-[12px]"
                />
                <div className="mt-1 text-[11px] text-ink-muted">
                  From the Airtel DLT portal. This is what the phone <strong>operator</strong> checks the
                  delivered text against.
                </div>
              </FormField>

              <FormField label="MSG91 template ID">
                <Input
                  value={editing.msg91_template_id ?? ''}
                  onChange={(e) => edit('msg91_template_id', e.target.value || null)}
                  placeholder="68b4f1c2d6fc0518ea1f2ab2"
                  className="font-mono text-[12px]"
                />
                <div className="mt-1 text-[11px] text-ink-muted">
                  A <strong>different</strong> id, from MSG91&apos;s own panel (SMS → Templates → Create
                  Template). Name its variables <code>var1</code>…<code>var{blanks || 'N'}</code> in the
                  same order as the blanks above.
                </div>
              </FormField>
            </div>

            <FormField label="Notes">
              <Input value={editing.notes ?? ''} onChange={(e) => edit('notes', e.target.value || null)} />
            </FormField>

            <label className="flex items-center justify-between gap-4 border-t border-border pt-4">
              <div>
                <div className="text-[13px] font-semibold">In use</div>
                <div className="mt-0.5 text-[12px] text-ink-muted">
                  Switched off, this wording is never sent — existing log entries still refer to it.
                </div>
              </div>
              <Switch checked={editing.active} onCheckedChange={(v) => edit('active', v)} aria-label="In use" />
            </label>

            {save.isError && !(save.error instanceof ApiError && Object.keys(save.error.fieldErrors).length) && (
              <div className="rounded-xl border border-danger-soft bg-danger-soft p-3 text-[13px] text-danger-ink">
                ✗ {save.error instanceof ApiError ? save.error.message : 'Could not save.'}
              </div>
            )}

            <div className="flex gap-2">
              <Button onClick={onSave} loading={save.isPending}>Save wording</Button>
              {!!editing.id && editing.active && (
                <Button
                  variant="ghost"
                  onClick={() => deactivate.mutateAsync(editing.id).then(() => setEditing(null))}
                  loading={deactivate.isPending}
                >
                  Switch off
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* ── Check it before it costs anything ─────────────────── */}
        {!!editing.id && (
          <Card>
            <CardHeader>
              <CardTitle>Check this wording</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="text-[12px] text-ink-muted">
                Fill in sample values to see exactly what the patient would read, whether the fixed words
                still match what was registered, and what it costs. Nothing is sent by validating.
              </div>

              {blanks === 0 ? (
                <div className="text-[12px] text-ink-muted">This wording has no blanks to fill.</div>
              ) : (
                <div className="grid2">
                  {values.map((v, i) => (
                    <FormField key={i} label={`Blank ${i + 1}`}>
                      <Input value={v} onChange={(e) => setValues((p) => p.map((x, j) => (j === i ? e.target.value : x)))} />
                    </FormField>
                  ))}
                </div>
              )}

              <Button variant="ghost" onClick={runPreview} loading={previewing}>Validate</Button>

              {preview && (
                <div
                  className={
                    'rounded-xl border p-3 ' +
                    (preview.valid ? 'border-border bg-muted' : 'border-danger-soft bg-danger-soft')
                  }
                >
                  {preview.valid ? (
                    <>
                      <div className="text-[11px] font-semibold uppercase tracking-wide text-ink-muted">
                        As the patient would read it
                      </div>
                      <div className="mt-1.5 whitespace-pre-wrap font-mono text-[12px]">{preview.text}</div>
                      <div className="mt-3 flex flex-wrap items-center gap-2 text-[11px]">
                        <Badge variant="success">Wording verified</Badge>
                        <Badge variant={preview.encoding === 'UCS2' ? 'warn' : 'neutral'}>
                          {preview.encoding === 'UCS2' ? 'Special characters — 70 per segment' : 'Plain text — 160 per segment'}
                        </Badge>
                        <Badge variant={(preview.segments ?? 1) > 1 ? 'warn' : 'neutral'}>
                          {preview.segments} segment{preview.segments === 1 ? '' : 's'} · {preview.units} of {preview.per_segment}
                        </Badge>
                      </div>
                      {!!preview.non_gsm_characters?.length && (
                        <div className="mt-2 text-[11px] text-warn-ink">
                          Costed at the higher rate because of: {preview.non_gsm_characters.map((c) => `"${c}"`).join(', ')}
                        </div>
                      )}
                      {!!preview.blockers?.length && (
                        <ul className="mt-2 list-disc space-y-1 pl-4 text-[11px] text-warn-ink">
                          {preview.blockers.map((b) => <li key={b.code}>{b.message}</li>)}
                        </ul>
                      )}
                    </>
                  ) : (
                    <div className="text-[12px] text-danger-ink">✗ {preview.error}</div>
                  )}
                </div>
              )}

              {/* ── Send test ─────────────────────────────────── */}
              <div className="border-t border-border pt-4">
                <div className="text-[13px] font-semibold">Send a test</div>
                <div className="mt-0.5 mb-2.5 text-[12px] text-ink-muted">
                  Goes through the same path a real send does. While <strong>Simulate only</strong> is on
                  this comes back <code>simulated</code>: built, verified and logged, nothing delivered and
                  nothing charged. Send to a staff mobile first.
                </div>
                <div className="flex flex-wrap items-end gap-2">
                  <FormField label="Mobile number" className="w-56">
                    <Input value={mobile} onChange={(e) => setMobile(e.target.value)} placeholder="9876543210" />
                  </FormField>
                  <Button variant="ghost" onClick={runTestSend} loading={sending} disabled={!mobile}>
                    Send test
                  </Button>
                </div>

                {sendError && (
                  <div className="mt-3 rounded-xl border border-danger-soft bg-danger-soft p-3 text-[12px] text-danger-ink">
                    ✗ {sendError}
                  </div>
                )}
                {sendResult && (
                  <div
                    className={
                      'mt-3 rounded-xl border p-3 text-[12px] ' +
                      (sendResult.status === 'sent'
                        ? 'border-success-soft bg-success-soft text-success-ink'
                        : sendResult.status === 'simulated'
                          ? 'border-warn-soft bg-warn-soft text-warn-ink'
                          : 'border-danger-soft bg-danger-soft text-danger-ink')
                    }
                  >
                    <div className="font-semibold">
                      {sendResult.status === 'sent'
                        ? `Delivered to ${sendResult.recipient}`
                        : sendResult.status === 'simulated'
                          ? 'Simulated — nothing was sent or charged'
                          : 'Not sent'}
                    </div>
                    {sendResult.failure_reason && <div className="mt-1">{sendResult.failure_reason}</div>}
                    {sendResult.message_text && (
                      <div className="mt-2 whitespace-pre-wrap font-mono text-[11px] opacity-90">
                        {sendResult.message_text}
                      </div>
                    )}
                    {sendResult.provider_message_id && (
                      <div className="mt-1 text-[11px] opacity-80">MSG91 reference {sendResult.provider_message_id}</div>
                    )}
                    <div className="mt-1 text-[11px] opacity-80">Recorded in the delivery log either way.</div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    );
  }

  // ── List ─────────────────────────────────────────────────────
  return (
    <div>
      <div className="mb-4 text-[13px] leading-relaxed text-ink-muted">
        The wordings this hospital is allowed to send. Each one must be registered twice — once on the
        Airtel DLT portal, which is what the phone operator checks the delivered text against, and once in
        MSG91, which is what accepts the send. A wording missing either id is built and logged but never
        delivered.
      </div>

      <Card>
        <CardHeader>
          <CardTitle><MessageSquareText size={15} /> Approved wordings</CardTitle>
          <Button size="sm" variant="ghost" onClick={() => open(BLANK)}>
            <Plus size={14} /> New wording
          </Button>
        </CardHeader>

        {rows.length === 0 ? (
          <EmptyState label="No SMS wordings yet" hint="Add the text exactly as approved on the DLT portal." />
        ) : (
          <div className="divide-y divide-border">
            {rows.map((t) => (
              <button
                key={t.id}
                onClick={() => open(t)}
                className="flex w-full items-start justify-between gap-4 p-4 text-left hover:bg-muted"
              >
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-[13px] font-semibold">{t.name}</span>
                    <code className="text-[11px] text-ink-muted">{t.template_key}</code>
                    {t.ready ? (
                      <Badge variant="success">Ready to send</Badge>
                    ) : !t.active ? (
                      <Badge variant="neutral">Switched off</Badge>
                    ) : (
                      <Badge variant="warn">Not registered</Badge>
                    )}
                    {t.encoding === 'UCS2' && <Badge variant="warn">Costs 3x</Badge>}
                  </div>
                  <div className="mt-1 truncate font-mono text-[11px] text-ink-muted">{t.body}</div>
                  {t.blockers[0] && (
                    <div className="mt-1 text-[11px] text-warn-ink">{t.blockers[0].message}</div>
                  )}
                </div>
                <div className="shrink-0 text-[11px] text-ink-muted">
                  {t.variable_count} blank{t.variable_count === 1 ? '' : 's'}
                </div>
              </button>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
